import { useCallback, useMemo, useRef } from 'react';
import { useAuthContext } from '@context/AuthContextProvider';
import { AccountType } from '@context/AvContext.types';
import { FeatureFlags } from '@fabric-ui/components';
import { useNavigate } from '@utils/AvRouter';
import { getPathNameByAccount } from '@utils/router.utils';
import { getDaysUntilOrSinceExpiration } from '@utils/utils';
import { useSnackbar } from 'notistack';
import { APP_PATHS, PAGE_PATHS } from '../../types';
import { AccountExpirationSnackbar } from './AccountExpirationSnackbar';

type AccountExpirationData = {
  expiresAt?: string | null;
  accountType: AccountType;
};

export const useAccountExpirationSnackbar = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { accountId, featureFlags, isLoading, userPermissions } = useAuthContext();

  const getPathName = useMemo(() => getPathNameByAccount(accountId, APP_PATHS.SETTINGS), [accountId]);

  const lastSnackbarKeyRef = useRef<string | number | undefined>(undefined);

  const close = useCallback(
    (key?: string | number) => {
      if (key != null) closeSnackbar(key);
    },
    [closeSnackbar]
  );

  const show = (accountData: AccountExpirationData) => {
    const deletionThresholdDays = 45;
    if (!isLoading && userPermissions.isInternalRole && featureFlags[FeatureFlags.AccountExpiraionLifecycle]) {
      const daysToExpiration = getDaysUntilOrSinceExpiration(accountData.expiresAt, 'until');
      const daysSinceExpiration = getDaysUntilOrSinceExpiration(accountData.expiresAt, 'since');

      if (daysToExpiration != null && daysSinceExpiration != null) {
        const shouldShowWarning =
          accountData.accountType === AccountType.POC && daysToExpiration >= 0 && daysToExpiration <= deletionThresholdDays;

        const shouldShowFinalWarning =
          accountData.accountType === AccountType.EXPIRED && daysSinceExpiration >= 0 && daysSinceExpiration <= deletionThresholdDays;

        if (shouldShowFinalWarning) {
          return enqueueSnackbar(
            <AccountExpirationSnackbar
              prefix={`This account has expired and is scheduled for permanent deletion in ${Math.max(
                0,
                deletionThresholdDays - daysSinceExpiration
              )} days. To roll back this expiration, please change the account status.`}
            />,
            {
              variant: 'error',
              persist: true,
              preventDuplicate: true,
            }
          );
        }
        if (shouldShowWarning) {
          return enqueueSnackbar(
            <AccountExpirationSnackbar
              prefix={`This account expires in ${daysToExpiration} days. `}
              actionText="Request an extension"
              suffix=" to keep it active, otherwise it will expire as scheduled."
              onClick={() =>
                navigate({
                  to: getPathName(PAGE_PATHS.EDIT_MY_ACCOUNT_MANAGEMENT, '', APP_PATHS.SETTINGS).to,
                })
              }
            />,
            {
              variant: 'warning',
              persist: true,
              preventDuplicate: true,
            }
          );
        }
      }
    }
    return undefined;
  };
  const onAccountSuccess = useCallback(
    (accountData: AccountExpirationData) => {
      close(lastSnackbarKeyRef.current);
      lastSnackbarKeyRef.current = show(accountData);
    },
    [close, show]
  );

  return { onAccountSuccess };
};
